import type { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import { Show } from '@elfsight/lib-utils';
import { MediaImage } from '@elfsight/widget-media';
import type { PriceProps } from '@elfsight/widget-ui';
import { getTokenFontStyle, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { TableLayout } from '../../../common';
import { STRETCH_FIT_MIXIN } from '../../../common';
import { useCheckoutColors, useCheckoutI18n } from '../providers';

const COMPONENT_NAME = 'table-value';
const INLINE_VALUES_DIVIDER = ', ';

const PRICE_ALIGNMENT: Record<TableLayout, CSSProperties> = {
  horizontal: { justifyContent: 'flex-end' },
  vertical: { justifyContent: 'flex-start' }
};

export interface TableValueProps {
  layout: TableLayout;
  value?: string;
  price?: PriceProps;
  imageSrc?: string;
  inline?: boolean;
  last?: boolean;
}

export function TableValue({
  value,
  price,
  imageSrc,
  layout,
  inline = false,
  last = false
}: TableValueProps) {
  const { language } = useSettings<{ language: LanguageValue }>();
  const { textColor } = useCheckoutColors();
  const { PriceComponent } = useCheckoutI18n();

  const withImage = !!imageSrc;
  const withValue = !!value;
  const withPrice = !!price;

  const withNowrap = !inline && layout === 'horizontal';

  if (!withImage && !withValue && !withPrice) {
    return null;
  }

  return (
    <Component $withNowrap={withNowrap} $inline={inline}>
      <Show when={withImage}>
        <ItemImage
          src={imageSrc}
          alt={value || ''}
          mode="cover"
          containerWidth="32px"
          containerHeight="32px"
          withSkeleton
        />
      </Show>

      <Show when={withValue || withPrice}>
        <Content $inline={inline}>
          <Show when={withValue}>
            <Value
              textToken="text16"
              color={textColor}
              dangerouslySetInnerHTML={{ __html: value! }}
            />
          </Show>

          <Show when={withPrice}>
            <PriceComponent
              {...price!}
              language={language}
              valueColor={textColor}
              affixColor={textColor}
              valueFont={getTokenFontStyle('text16')}
              style={PRICE_ALIGNMENT[layout]}
              className={createReadableClass({
                component: COMPONENT_NAME,
                element: 'price'
              })}
            />
          </Show>
        </Content>
      </Show>

      <Show when={inline && !last}>
        <InlineValueDivider textToken="text16" color={textColor}>
          {INLINE_VALUES_DIVIDER}
        </InlineValueDivider>
      </Show>
    </Component>
  );
}

const Component = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})<{ $withNowrap?: boolean; $inline?: boolean }>`
  flex-direction: row;
  align-items: center;

  ${({ $withNowrap }) => $withNowrap && 'white-space: nowrap;'}
  ${({ $inline }) => {
    if ($inline) {
      return css`
        display: inline;
      `;
    }

    return css`
      display: flex;
      gap: 8px;
    `;
  }}

  ${STRETCH_FIT_MIXIN}
`;

const ItemImage = styled(MediaImage).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'image'
  })
})`
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
`;

const Content = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'content'
  })
})<{ $inline?: boolean }>`
  flex-direction: column;
  flex: 1;

  ${({ $inline }) => {
    if ($inline) {
      return css`
        display: inline-block;
      `;
    }

    return css`
      display: flex;
      gap: 4px;
    `;
  }}

  ${STRETCH_FIT_MIXIN}
`;

const Value = styled(Typography).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'text'
  })
})`
  ${STRETCH_FIT_MIXIN}
`;

const InlineValueDivider = styled(Typography).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'divider'
  })
})`
  display: inline;
`;
